import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/lib/SessionContext';
import { getPageAccess } from '@/lib/AuthSession';
import { Button } from '@/components/ui/button';
import { 
  ShieldCheck, LayoutDashboard, Calculator, 
  Lock, LogOut, ChevronRight, Sparkles, User, TrendingUp, ShoppingCart
} from 'lucide-react';
import AsaGreetingWidget from '@/components/AsaGreetingWidget';
import ClockInButton from '@/components/layout/ClockInButton';

export default function OfficeGateway() {
  const { member, logout } = useSession();
  const navigate = useNavigate();

  // Dynamic access checks
  const hasAccess = (portal) => {
    if (!member) return false;

    // Developer & Owner always have access to all portals
    const isDev = member.role === 'developer' || member.role === 'owner' || 
                  (member.department && member.department.toLowerCase().includes('developer')) ||
                  (member.position && member.position.toLowerCase().includes('developer'));
                  
    if (isDev) {
      return true;
    }

    if (portal === 'workspace') {
      return getPageAccess(member, 'portal_workspace') !== 'none';
    }
    if (portal === 'finance') {
      return getPageAccess(member, 'portal_finance') !== 'none';
    }
    if (portal === 'production') {
      return getPageAccess(member, 'portal_production') !== 'none';
    }
    if (portal === 'marketing') {
      return getPageAccess(member, 'portal_marketing') !== 'none';
    }
    if (portal === 'purchasing') {
      return getPageAccess(member, 'portal_purchasing') !== 'none';
    }
    if (portal === 'analytics') {
      return getPageAccess(member, 'portal_analytics') !== 'none' || member.role === 'finance';
    }
    return false;
  };

  const handlePortalClick = (portal, path) => {
    if (hasAccess(portal)) {
      navigate(path);
    }
  };

  const getRoleLabel = (r) => {
    if (r === 'developer') return 'Developer Utama';
    if (r === 'owner') return 'Owner / Pemilik Pabrik';
    if (r === 'finance') return 'Staff Finance';
    if (r === 'marketing') return 'Staff Marketing';
    return 'Staff Produksi / Operasional';
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#302621] font-sans flex flex-col relative overflow-hidden">
      
      {/* Background Decorative Gradients */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-[#E6D5B8]/20 to-transparent rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-tr from-[#D4AF37]/10 to-transparent rounded-full blur-3xl pointer-events-none" />

      {/* Header Bar */}
      <header className="relative z-10 border-b border-[#E6D5B8]/40 bg-white/75  px-2 md:px-6 py-1.5 md:py-4 flex items-center justify-between shadow-xs">
        <div className="flex items-center gap-1 md:gap-3">
          <div className="w-4 h-4 md:w-8 md:h-8 flex items-center justify-center">
            <img src="/logo.png" alt="Satala Logo" className="w-full h-full object-contain drop-shadow-sm" />
          </div>
          <div className="flex flex-col">
            <span className="text-[9px] md:text-sm font-black tracking-tight text-[#302621] leading-tight">Satala Office</span>
            <span className="text-[5px] md:text-[8px] text-[#8C6208] font-bold tracking-widest uppercase leading-none mt-0.5">Pusat Informasi & Kendali</span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 md:gap-4">
          <ClockInButton />
          {member && (
            <div className="flex items-center gap-1.5 md:gap-2.5 bg-[#F9F5EC] border border-[#E6D5B8]/40 px-2 md:px-3 py-1 md:py-1.5 rounded-lg md:rounded-xl">
              <User className="w-2.5 h-2.5 md:w-3.5 md:h-3.5 text-[#8C6208]" />
              <div className="text-left">
                <p className="text-[7px] md:text-[10px] font-black text-[#302621] leading-none">{member.full_name}</p>
                <p className="text-[5px] md:text-[8px] text-[#8C6208] font-bold mt-0.5 leading-none uppercase">{member.position || getRoleLabel(member.role)}</p>
              </div>
            </div>
          )}

          <Button
            variant="ghost"
            onClick={() => {
              logout?.();
              navigate('/login-tim');
            }}
            className="text-[7px] md:text-xs font-bold text-red-600 hover:text-red-700 hover:bg-red-50 gap-0.5 md:gap-1.5 px-2 md:px-3 h-[22px] md:h-9 rounded-lg md:rounded-xl border border-red-100"
          >
            <LogOut className="w-2.5 h-2.5 md:w-3.5 md:h-3.5" />
            <span>Keluar</span>
          </Button>
        </div>
      </header>

      {/* Main Container */}
      <main className="flex-1 max-w-5xl mx-auto w-full px-3 md:px-6 py-6 md:py-12 flex flex-col justify-center relative z-10 space-y-4 md:space-y-10">
        
        {/* Gateway Heading */}
        <div className="text-center space-y-1.5 md:space-y-3 flex flex-col items-center">
          <img src="/logo.png" alt="Satala Logo" className="w-10 h-10 md:w-20 md:h-20 object-contain mb-0.5 md:mb-2 drop-shadow-sm" />
          <div className="inline-flex items-center gap-1 md:gap-1.5 bg-[#D4AF37]/15 border border-[#D4AF37]/35 text-[#8C6208] px-2 md:px-3.5 py-0.5 md:py-1 rounded-full text-[7px] md:text-[10px] font-black tracking-widest uppercase mb-0.5 md:mb-1">
            <Sparkles className="w-2 h-2 md:w-3 md:h-3" /> SATALA ENTERPRISE GATEWAY
          </div>
          <h2 className="text-base md:text-3xl lg:text-4xl font-black text-[#302621] tracking-tight leading-tight">
            Selamat Datang di Hub Kantor
          </h2>
          <p className="text-[9px] md:text-xs text-[#7A695B] font-medium max-w-[260px] md:max-w-md mx-auto leading-relaxed">
            Silakan pilih portal tujuan di bawah ini. Akses Anda dikendalikan secara dinamis berdasarkan otorisasi peran masing-masing.
          </p>
        </div>

        <AsaGreetingWidget />

        {/* Portals Cards Grid */}
        <div className="flex flex-wrap justify-center gap-3 md:gap-6 max-w-5xl mx-auto w-full">
          
          {/* 1. PORTAL PRODUKSI */}
          {(() => {
            const open = hasAccess('production');
            if (!open) return null;
            return (
              <div 
                onClick={() => handlePortalClick('production', '/office/production')}
                className={`relative rounded-[16px] md:rounded-3xl p-3.5 md:p-6 border transition-all duration-300 flex flex-col min-h-[170px] md:min-h-72 w-full md:w-[calc(50%-12px)] lg:w-[calc(33.33%-16px)] max-w-[300px] shadow-xs group ${
                  open 
                    ? 'bg-white border-[#E6D5B8] hover:border-[#8C6208] hover:shadow-md cursor-pointer' 
                    : 'bg-[#F2ECE4]/40 border-slate-200/80 opacity-65 cursor-not-allowed select-none'
                }`}
              >
                {/* Visual Status Indicator */}
                <div className="flex justify-between items-start">
                  <div className={`w-8 h-8 md:w-12 md:h-12 rounded-[10px] md:rounded-2xl flex items-center justify-center shadow-xs transition-colors ${
                    open ? 'bg-[#F9F5EC] text-[#8C6208] group-hover:bg-[#8C6208] group-hover:text-white' : 'bg-slate-200 text-slate-400'
                  }`}>
                    <LayoutDashboard className="w-4 h-4 md:w-6 md:h-6" />
                  </div>
                  {!open ? (
                    <BadgeIcon label="Terkunci" color="red" />
                  ) : (
                    <BadgeIcon label="Akses Aktif" color="green" />
                  )}
                </div>

                <div className="mt-3 md:mt-8 space-y-0.5 md:space-y-1.5">
                  <h3 className={`text-[13px] md:text-lg font-black tracking-tight ${open ? 'text-[#302621]' : 'text-slate-500'}`}>
                    Sistem Produksi
                  </h3>
                  <p className="text-[9px] md:text-xs text-[#7A695B] font-medium leading-relaxed">
                    Kelola kalkulator HPP, stok bahan baku, resep racikan, delivery order, dan tugas harian pabrik.
                  </p>
                </div>

                {/* Footer status / button */}
                <div className="mt-auto pt-2.5 md:pt-4 flex items-center justify-between border-t border-slate-100/80">
                  {open ? (
                    <>
                      <span className="text-[8px] md:text-[10px] font-black text-[#8C6208] uppercase tracking-wider">Masuk Portal</span>
                      <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-[#8C6208] transition-transform group-hover:translate-x-1" />
                    </>
                  ) : (
                    <LockedLabel />
                  )}
                </div>
              </div>
            );
          })()}

          {/* 2. PORTAL FINANCE */}
          {(() => {
            const open = hasAccess('finance');
            if (!open) return null;
            return (
              <div 
                onClick={() => handlePortalClick('finance', '/office/finance')}
                className={`relative rounded-[16px] md:rounded-3xl p-3.5 md:p-6 border transition-all duration-300 flex flex-col min-h-[170px] md:min-h-72 w-full md:w-[calc(50%-12px)] lg:w-[calc(33.33%-16px)] max-w-[300px] shadow-xs group ${
                  open 
                    ? 'bg-white border-[#E6D5B8] hover:border-[#8C6208] hover:shadow-md cursor-pointer' 
                    : 'bg-[#F2ECE4]/40 border-slate-200/80 opacity-65 cursor-not-allowed select-none'
                }`}
              >
                {/* Visual Status Indicator */}
                <div className="flex justify-between items-start">
                  <div className={`w-8 h-8 md:w-12 md:h-12 rounded-[10px] md:rounded-2xl flex items-center justify-center shadow-xs transition-colors ${
                    open ? 'bg-[#F9F5EC] text-[#8C6208] group-hover:bg-[#8C6208] group-hover:text-white' : 'bg-slate-200 text-slate-400'
                  }`}>
                    <Calculator className="w-4 h-4 md:w-6 md:h-6" />
                  </div>
                  {!open ? (
                    <BadgeIcon label="Terkunci" color="red" />
                  ) : (
                    <BadgeIcon label="Akses Aktif" color="green" />
                  )}
                </div>

                <div className="mt-3 md:mt-8 space-y-0.5 md:space-y-1.5">
                  <h3 className={`text-[13px] md:text-lg font-black tracking-tight ${open ? 'text-[#302621]' : 'text-slate-500'}`}>
                    Keuangan & Finance
                  </h3>
                  <p className="text-[9px] md:text-xs text-[#7A695B] font-medium leading-relaxed">
                    Akses dashboard pembukuan keuangan pabrik, pencatatan transaksi masuk/keluar, dan laporan omset.
                  </p>
                </div>

                {/* Footer status / button */}
                <div className="mt-auto pt-2.5 md:pt-4 flex items-center justify-between border-t border-slate-100/80">
                  {open ? (
                    <>
                      <span className="text-[8px] md:text-[10px] font-black text-[#8C6208] uppercase tracking-wider">Masuk Portal</span>
                      <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-[#8C6208] transition-transform group-hover:translate-x-1" />
                    </>
                  ) : (
                    <LockedLabel />
                  )}
                </div>
              </div>
            );
          })()}

          {/* 3. PORTAL WORKSPACE OWNER */}
          {(() => {
            const open = hasAccess('workspace');
            if (!open) return null;
            return (
              <div 
                onClick={() => handlePortalClick('workspace', '/office/workspace')}
                className={`relative rounded-[16px] md:rounded-3xl p-3.5 md:p-6 border transition-all duration-300 flex flex-col min-h-[170px] md:min-h-72 w-full md:w-[calc(50%-12px)] lg:w-[calc(33.33%-16px)] max-w-[300px] shadow-xs group ${
                  open 
                    ? 'bg-white border-[#E6D5B8] hover:border-[#8C6208] hover:shadow-md cursor-pointer' 
                    : 'bg-[#F2ECE4]/40 border-slate-200/80 opacity-65 cursor-not-allowed select-none'
                }`}
              >
                {/* Visual Status Indicator */}
                <div className="flex justify-between items-start">
                  <div className={`w-8 h-8 md:w-12 md:h-12 rounded-[10px] md:rounded-2xl flex items-center justify-center shadow-xs transition-colors ${
                    open ? 'bg-[#F9F5EC] text-[#8C6208] group-hover:bg-[#8C6208] group-hover:text-white' : 'bg-slate-200 text-slate-400'
                  }`}>
                    <ShieldCheck className="w-4 h-4 md:w-6 md:h-6" />
                  </div>
                  {!open ? (
                    <BadgeIcon label="Terkunci" color="red" />
                  ) : (
                    <BadgeIcon label="Akses Aktif" color="green" />
                  )}
                </div>

                <div className="mt-3 md:mt-8 space-y-0.5 md:space-y-1.5">
                  <h3 className={`text-[13px] md:text-lg font-black tracking-tight ${open ? 'text-[#302621]' : 'text-slate-500'}`}>
                    Workspace Admin
                  </h3>
                  <p className="text-[9px] md:text-xs text-[#7A695B] font-medium leading-relaxed">
                    Kendali khusus Owner & Dev: Kelola mitra terverifikasi pabrik, hak akses tim, dan logo/cap stempel invoice.
                  </p>
                </div>

                {/* Footer status / button */}
                <div className="mt-auto pt-2.5 md:pt-4 flex items-center justify-between border-t border-slate-100/80">
                  {open ? (
                    <>
                      <span className="text-[8px] md:text-[10px] font-black text-[#8C6208] uppercase tracking-wider">Masuk Portal</span>
                      <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-[#8C6208] transition-transform group-hover:translate-x-1" />
                    </>
                  ) : (
                    <LockedLabel />
                  )}
                </div>
              </div>
            );
          })()}

          {/* 4. PORTAL MARKETING */}
          {(() => {
            const open = hasAccess('marketing');
            if (!open) return null;
            return (
              <div 
                onClick={() => handlePortalClick('marketing', '/office/marketing')}
                className={`relative rounded-[16px] md:rounded-3xl p-3.5 md:p-6 border transition-all duration-300 flex flex-col min-h-[170px] md:min-h-72 w-full md:w-[calc(50%-12px)] lg:w-[calc(33.33%-16px)] max-w-[300px] shadow-xs group ${
                  open 
                    ? 'bg-white border-[#E6D5B8] hover:border-[#8C6208] hover:shadow-md cursor-pointer' 
                    : 'bg-[#F2ECE4]/40 border-slate-200/80 opacity-65 cursor-not-allowed select-none'
                }`}
              >
                {/* Visual Status Indicator */}
                <div className="flex justify-between items-start">
                  <div className={`w-8 h-8 md:w-12 md:h-12 rounded-[10px] md:rounded-2xl flex items-center justify-center shadow-xs transition-colors ${
                    open ? 'bg-[#F9F5EC] text-[#8C6208] group-hover:bg-[#8C6208] group-hover:text-white' : 'bg-slate-200 text-slate-400'
                  }`}>
                    <TrendingUp className="w-4 h-4 md:w-6 md:h-6" />
                  </div>
                  {!open ? (
                    <BadgeIcon label="Terkunci" color="red" />
                  ) : (
                    <BadgeIcon label="Akses Aktif" color="green" />
                  )}
                </div>

                <div className="mt-3 md:mt-8 space-y-0.5 md:space-y-1.5">
                  <h3 className={`text-[13px] md:text-lg font-black tracking-tight ${open ? 'text-[#302621]' : 'text-slate-500'}`}>
                    Marketing & Sales
                  </h3>
                  <p className="text-[9px] md:text-xs text-[#7A695B] font-medium leading-relaxed">
                    Akses rekap penjualan, laporan performa brand, dan kelola invoice klien.
                  </p>
                </div>

                {/* Footer status / button */}
                <div className="mt-auto pt-2.5 md:pt-4 flex items-center justify-between border-t border-slate-100/80">
                  {open ? (
                    <>
                      <span className="text-[8px] md:text-[10px] font-black text-[#8C6208] uppercase tracking-wider">Masuk Portal</span>
                      <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-[#8C6208] transition-transform group-hover:translate-x-1" />
                    </>
                  ) : (
                    <LockedLabel />
                  )}
                </div>
              </div>
            );
          })()}

          {/* 5. PORTAL PURCHASING */}
          {(() => {
            const open = hasAccess('purchasing');
            if (!open) return null;
            return (
              <div 
                onClick={() => handlePortalClick('purchasing', '/office/purchasing')}
                className={`relative rounded-[16px] md:rounded-3xl p-3.5 md:p-6 border transition-all duration-300 flex flex-col min-h-[170px] md:min-h-72 w-full md:w-[calc(50%-12px)] lg:w-[calc(33.33%-16px)] max-w-[300px] shadow-xs group ${
                  open 
                    ? 'bg-white border-[#E6D5B8] hover:border-[#8C6208] hover:shadow-md cursor-pointer' 
                    : 'bg-[#F2ECE4]/40 border-slate-200/80 opacity-65 cursor-not-allowed select-none'
                }`}
              >
                {/* Visual Status Indicator */}
                <div className="flex justify-between items-start">
                  <div className={`w-8 h-8 md:w-12 md:h-12 rounded-[10px] md:rounded-2xl flex items-center justify-center shadow-xs transition-colors ${
                    open ? 'bg-[#F9F5EC] text-[#8C6208] group-hover:bg-[#8C6208] group-hover:text-white' : 'bg-slate-200 text-slate-400'
                  }`}>
                    <ShoppingCart className="w-4 h-4 md:w-6 md:h-6" />
                  </div>
                  {!open ? (
                    <BadgeIcon label="Terkunci" color="red" />
                  ) : (
                    <BadgeIcon label="Akses Aktif" color="green" />
                  )}
                </div>

                <div className="mt-3 md:mt-8 space-y-0.5 md:space-y-1.5">
                  <h3 className={`text-[13px] md:text-lg font-black tracking-tight ${open ? 'text-[#302621]' : 'text-slate-500'}`}>
                    Purchasing
                  </h3>
                  <p className="text-[9px] md:text-xs text-[#7A695B] font-medium leading-relaxed">
                    Kelola barang yang harus dibeli, rekomendasi stok habis, dan tracking status pengadaan bahan.
                  </p>
                </div>

                {/* Footer status / button */}
                <div className="mt-auto pt-2.5 md:pt-4 flex items-center justify-between border-t border-slate-100/80">
                  {open ? (
                    <>
                      <span className="text-[8px] md:text-[10px] font-black text-[#8C6208] uppercase tracking-wider">Masuk Portal</span>
                      <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-[#8C6208] transition-transform group-hover:translate-x-1" />
                    </>
                  ) : (
                    <LockedLabel />
                  )}
                </div>
              </div>
            );
          })()}

          {/* 6. PORTAL ANALYTICS MANAGEMENT */}
          {(() => {
            const open = hasAccess('analytics');
            if (!open) return null;
            return (
              <div 
                onClick={() => handlePortalClick('analytics', '/office/analytics')}
                className={`relative rounded-[16px] md:rounded-3xl p-3.5 md:p-6 border transition-all duration-300 flex flex-col min-h-[170px] md:min-h-72 w-full md:w-[calc(50%-12px)] lg:w-[calc(33.33%-16px)] max-w-[300px] shadow-xs group ${
                  open 
                    ? 'bg-gradient-to-br from-[#FFFDF9] to-[#F9F5EC] border-[#D4AF37] hover:border-[#8C6208] hover:shadow-lg cursor-pointer' 
                    : 'bg-[#F2ECE4]/40 border-slate-200/80 opacity-65 cursor-not-allowed select-none'
                }`}
              >
                {/* Visual Status Indicator */}
                <div className="flex justify-between items-start">
                  <div className={`w-8 h-8 md:w-12 md:h-12 rounded-[10px] md:rounded-2xl flex items-center justify-center shadow-md transition-colors ${
                    open ? 'bg-[#D4AF37] text-white group-hover:bg-[#8C6208]' : 'bg-slate-200 text-slate-400'
                  }`}>
                    <TrendingUp className="w-4 h-4 md:w-6 md:h-6" />
                  </div>
                  {!open ? (
                    <BadgeIcon label="Terkunci" color="red" />
                  ) : (
                    <BadgeIcon label="Akses Eksklusif" color="green" />
                  )}
                </div>

                <div className="mt-3 md:mt-8 space-y-0.5 md:space-y-1.5">
                  <h3 className={`text-[13px] md:text-lg font-black tracking-tight ${open ? 'text-[#8C6208]' : 'text-slate-500'}`}>
                    Analytics Management
                  </h3>
                  <p className="text-[9px] md:text-xs text-[#7A695B] font-medium leading-relaxed">
                    Pusat data konfidensial. Akses Margin PO, Profit Sample, dan Papan Peringkat Omset secara real-time.
                  </p>
                </div>

                {/* Footer status / button */}
                <div className="mt-auto pt-2.5 md:pt-4 flex items-center justify-between border-t border-[#E6D5B8]/50">
                  {open ? (
                    <>
                      <span className="text-[8px] md:text-[10px] font-black text-[#8C6208] uppercase tracking-wider">Masuk Portal</span>
                      <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-[#8C6208] transition-transform group-hover:translate-x-1" />
                    </>
                  ) : (
                    <LockedLabel />
                  )}
                </div>
              </div>
            );
          })()}

        </div>

      </main>

      {/* Footer Info */}
      <footer className="relative z-10 text-center py-4 md:py-6 border-t border-[#E6D5B8]/20 text-[8px] md:text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-auto">
        © 2026 PT. Satala Dermatech Essential. All rights reserved.
      </footer>

    </div>
  );
}

// Micro Helper Components
function BadgeIcon({ label, color }) {
  const isGreen = color === 'green';
  return (
    <span className={`text-[8px] font-black px-2 py-0.5 rounded-full border tracking-wide uppercase ${
      isGreen 
        ? 'bg-emerald-50 text-emerald-800 border-emerald-300' 
        : 'bg-red-50 text-red-800 border-red-200'
    }`}>
      {label}
    </span>
  );
}

function LockedLabel() {
  return (
    <div className="flex items-center gap-1.5 text-[10px] text-red-600 font-black uppercase">
      <Lock className="w-3.5 h-3.5" />
      <span>Akses Terkunci</span>
    </div>
  );
}

function LockedLabelDescription() {
  return (
    <div className="bg-red-50/50 border border-red-200/30 rounded-xl px-3 py-2 text-[10px] text-red-800/85">
      Hubungi Owner jika Anda memerlukan akses ke portal ini.
    </div>
  );
}
